import { Config } from '@/config';
import { ApiService } from '@/services/api.service';

/**
 * Billing reads and linking actions against the auth API (issue #2522).
 *
 * Every endpoint here is admin-only except `getMine`, which takes no tenant parameter at all — the
 * backend reads the tenant from the JWT. That asymmetry is deliberate: there is no call in this file
 * that lets a caller name a tenant other than through an admin-gated route.
 */
class BillingService {
  /** The admin Subscriptions table: every tenant, whether or not it has a Stripe customer. */
  getTenants({ needsAttentionOnly = false } = {}) {
    return ApiService.get(`${Config.api.auth.url}billing/tenants`, {
      params: { needsAttentionOnly },
    }).then((response) => response.data.map(mapTenantBilling));
  }

  /**
   * Active tenants past their trial with no billing relationship — every row is a tenant using the
   * product for free. Answers correctly before any webhook has arrived.
   */
  getUnbilledPastTrial() {
    return ApiService.get(`${Config.api.auth.url}billing/tenants/unbilled`).then((response) =>
      response.data.map(mapTenantBilling),
    );
  }

  /** Stripe customers with no tenant attached — the candidates for the link dialog. */
  getUnlinkedCustomers() {
    return ApiService.get(`${Config.api.auth.url}billing/customers/unlinked`).then((response) => response.data);
  }

  /**
   * Tonight's job, now: retry stuck webhook events, discover Stripe customers we do not hold, refresh
   * every linked customer and the unlinked live ones. Read-only against Stripe. Resolves with the run
   * (`found, skipped, synced, refreshed, failed, eventsPending, eventsRetried`) and the digest counts.
   */
  sync() {
    return ApiService.post(`${Config.api.auth.url}billing/sync`).then((response) => response.data);
  }

  /**
   * Resolves on success. Rejects with 409 when either side is already linked, 404 when Stripe has no
   * such customer — the backend fetches an unknown id from Stripe before linking, so a pasted
   * `cus_…` works without an import first.
   */
  linkCustomer({ tenantId, stripeCustomerId }) {
    return ApiService.post(`${Config.api.auth.url}billing/tenants/${tenantId}/link`, {
      stripeCustomerId,
    });
  }

  unlinkCustomer(tenantId) {
    return ApiService.delete(`${Config.api.auth.url}billing/tenants/${tenantId}/link`);
  }

  /** Re-fetch this tenant from Stripe now, for when a webhook was missed. */
  resync(tenantId) {
    return ApiService.post(`${Config.api.auth.url}billing/tenants/${tenantId}/resync`);
  }

  /**
   * A Payment Link URL with `client_reference_id` already appended.
   *
   * Rejects with 404 when no link is configured for that combination — surfaced rather than
   * swallowed, because a bare link would produce an unlinked Stripe customer.
   */
  getPaymentLink({ tenantId, planCode, interval, bi = false }) {
    return ApiService.get(`${Config.api.auth.url}billing/payment-link`, {
      params: { tenantId, planCode, interval, bi },
    }).then((response) => response.data.url);
  }

  /** The caller's own tenant billing state, for the billing page and the banners. */
  getMine() {
    return ApiService.get(`${Config.api.auth.url}billing/me`).then((response) => mapTenantBilling(response.data));
  }
}

/**
 * The backend already computes the derived states (`trialState`, `biState`, `needsAttention`), so
 * this only reshapes for display. Recomputing them here would put the same rules in two languages.
 */
function mapTenantBilling(row) {
  if (!row) return null;

  return {
    ...row,
    linked: !!row.stripeCustomerId,
    cardExpiry: formatCardExpiry(row.cardExpMonth, row.cardExpYear),
    cardExpiryTone: cardExpiryTone(row.cardExpMonth, row.cardExpYear),
    monthlyAmount: monthlyEquivalent(row.unitAmount, row.planInterval),
  };
}

function formatCardExpiry(month, year) {
  if (!month || !year) return null;
  return `${String(month).padStart(2, '0')}/${String(year).slice(-2)}`;
}

/**
 * Mirrors the backend threshold: warn from 60 days, escalate at 14, and treat already-expired as its
 * own state. A card is valid through the *end* of its expiry month, so compare against the first of
 * the following month rather than the first of that month.
 */
function cardExpiryTone(month, year) {
  if (!month || !year) return null;

  const expiresAfter = new Date(year, month, 1);
  const days = Math.ceil((expiresAfter - Date.now()) / 86400000);

  if (days <= 0) return 'expired';
  if (days <= 14) return 'critical';
  if (days <= 60) return 'warning';
  return 'ok';
}

/** Annual prices divided by 12 so a mixed-interval table can be compared down one column. */
function monthlyEquivalent(unitAmount, interval) {
  if (unitAmount == null) return null;
  const dollars = unitAmount / 100;
  return interval === 'year' ? dollars / 12 : dollars;
}

export const billingService = new BillingService();
